import type { Locale } from './config';
import { locales, defaultLocale } from './config';

export { locales, defaultLocale, type Locale };

/**
 * Flatten nested message objects into dot-notation keys.
 * e.g., { auth: { login: { title: "登录" } } } -> { "auth.login.title": "登录" }
 */
function flattenMessages(obj: unknown, prefix = ''): Record<string, string> {
  if (typeof obj !== 'object' || obj === null) {
    return prefix ? { [prefix]: obj as string } : {};
  }

  const result: Record<string, string> = {};
  const record = obj as Record<string, unknown>;

  for (const [key, value] of Object.entries(record)) {
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') {
      result[newKey] = value;
    } else if (typeof value === 'object' && value !== null) {
      Object.assign(result, flattenMessages(value, newKey));
    }
  }

  return result;
}

// Each locale is a single merged chunk — one network request per language switch
const localeLoaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  'zh-CN': () => import('./locales/zh-CN.json'),
  'en-US': () => import('./locales/en-US.json'),
  'zh-TW': () => import('./locales/zh-TW.json'),
  'ja': () => import('./locales/ja.json'),
  'ko': () => import('./locales/ko.json'),
  'fr': () => import('./locales/fr.json'),
  'es': () => import('./locales/es.json'),
  'pt': () => import('./locales/pt.json'),
  'de': () => import('./locales/de.json'),
  'it': () => import('./locales/it.json'),
  'ru': () => import('./locales/ru.json'),
  'ar': () => import('./locales/ar.json'),
  'th': () => import('./locales/th.json'),
  'vi': () => import('./locales/vi.json'),
  'id': () => import('./locales/id.json'),
  'hi': () => import('./locales/hi.json'),
  'nl': () => import('./locales/nl.json'),
  'tr': () => import('./locales/tr.json'),
  'pl': () => import('./locales/pl.json'),
};

const messagesCache = new Map<Locale, Record<string, string>>();

export async function getMessages(locale: Locale): Promise<Record<string, string>> {
  const cached = messagesCache.get(locale);
  if (cached) return cached;

  const loader = localeLoaders[locale];
  if (!loader) {
    // fallback to default locale
    return getMessages(defaultLocale);
  }

  const module = await loader();
  const messages = flattenMessages(module.default);

  messagesCache.set(locale, messages);
  return messages;
}
